// src/pages/cart/index.js
import { useNavigate } from "react-router-dom";
import Header from "../../components/header";
import image from "../../assets/image/cart.webp";
import "./cart.css";
import {
  Row,
  Col,
  Card,
  Table,
  Form,
  Spinner,
  Container,
} from "react-bootstrap";
import {
  getCartApi,
  updateCartApi,
  removeCartApi,
  applyDiscountApi,
  getAppSettings,
  createOrderApi,
} from "../../api/api";
import { useEffect, useRef, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import CloverCheckoutButton from "../../components/cloverButtonCheckout/CloverButtonCheckout";
import SpinLoading from "../../components/spinLoading/SpinLoading";
 
import { handleError } from "../../helpers/handleError";
import FiservCheckoutButton from "../../components/fiservButton/FiservButtonCheckout";
import { getStoredEventReferral } from "../../helpers/referral";
import {
  getResolvedMetaPixelId,
  loadMetaPixel,
  normalizeMetaCurrency,
  trackMetaPixel,
} from "../../helpers/metaPixel";
import {
  calculateChargedQuantity,
  calculateTicketLineTotal,
  getPromotionLabel,
  normalizeTicketPromotion,
} from "../../helpers/ticketPromotions";
const Cart = () => {
  const nav = [
    { title: "Inicio", link: "/event" },
    { title: "Compra de entradas", link: "/booking-ticket" },
  ];
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false); // para "Update Cart"
  const metaPixelCheckoutTrackedRef = useRef(false);

  const token = useSelector((state) => state.auth.accessToken);
  const currentUser = useSelector((state) => state.auth.user); // si tu store lo tiene

  const [cartData, setCartData] = useState([]);
  const [discountCode, setDiscountCode] = useState("");
  // hidden: we no longer show suggested coupons
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState("");
  const [discountSuccess, setDiscountSuccess] = useState({});
  const [appSettings, setAppSettings] = useState({});
  const [maxQuantityerror, setMaxQuantityerror] = useState("");
  const needsDniForCheckout = () => {
    if (!token) return false;
    const role = String(currentUser?.role || "").toUpperCase();
    if (role === "ORGANIZER") return false;
    const cleaned = String(currentUser?.dni || "").replace(/\D+/g, "");
    return !cleaned;
  };
  const guardDniForCheckout = () => {
    if (!needsDniForCheckout()) return true;
    toast.error("Necesitas agregar tu DNI en tu perfil para continuar.");
    navigate("/account/profile");
    return false;
  };

  // Helpers derivados del carrito (1 evento)
  const firstItem = cartData?.[0];
  const eventId = firstItem?.eventid?._id;
  const maxTicketsPerUser = Math.max(1, Math.trunc(Number(firstItem?.eventid?.maxTicketsPerUser || 4)));
  // Para soportar múltiples tipos en el mismo evento usamos la suma total
  const quantity = Array.isArray(cartData) && cartData.length > 0
    ? cartData.reduce((sum, it) => sum + (it?.quantity || 0), 0)
    : 1;
  const appliedCouponCode = discountCode || ""; // el que escribe el usuario
  const __unit = (typeof firstItem?.unitprice === 'number')
    ? firstItem.unitprice
    : (firstItem?.unitprice ? Number(firstItem.unitprice) : (firstItem?.eventid?.price || 0));
  const isFreeEvent = Boolean(firstItem?.eventid?.isFree || __unit === 0);
  const referralToken = eventId ? (getStoredEventReferral(eventId)?.token || "") : "";

  // Fetch cart items from API
  const fetchCart = async () => {
    try {
      setLoading(true);
      const res = await getCartApi(token);
      if (res.status === 200) {
        setCartData(res.data.info);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // removed: fetching public coupon to display on cart

  const fetchAppSettings = async () => {
    try {
      setLoading(true);
      const response = await getAppSettings();
      if (response.status === 200) setAppSettings(response.data.info);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();
    fetchAppSettings();
    window.scrollTo(0, 0);
  }, []);

  // Handle quantity change
  const handleQuantityChange = (index, newQuantity) => {
    const safeQuantity = Math.max(1, Number(newQuantity || 1));
    const nextTotal = cartData.reduce((sum, item, i) => sum + (i === index ? safeQuantity : Number(item.quantity || 0)), 0);
    if (nextTotal > maxTicketsPerUser) {
      setMaxQuantityerror(`Maximo ${maxTicketsPerUser} tickets por persona para este evento`);
      window.scrollTo(0, 0);
      return;
    }
    if (
      safeQuantity >
      cartData[0].eventid.maxbooking - cartData[0].eventid.totalbooking
    ) {
      setMaxQuantityerror(
        "Maximum quantity is " +
          (cartData[0].eventid.maxbooking - cartData[0].eventid.totalbooking)
      );
      window.scrollTo(0, 0);
      return;
    }
    setMaxQuantityerror("");
    const updatedCart = cartData.map((item, i) =>
      i === index ? { ...item, quantity: safeQuantity } : item
    );
    setCartData(updatedCart);
  };

  // Calculate price per item (supports ticket types)
  const getItemPrice = (item) => (typeof item.unitprice === 'number' ? item.unitprice : (item?.unitprice ? Number(item.unitprice) : (item?.eventid?.price || 0)));
  const getItemPromotion = (item) => normalizeTicketPromotion(item?.promotion);
  // Calculate subtotal per item
  const calculateSubtotal = (item) => calculateTicketLineTotal(getItemPrice(item), item.quantity, getItemPromotion(item));
  const getItemPromotionNote = (item) => {
    const label = getPromotionLabel(item?.promotion);
    if (!label) return "";
    const qty = Number(item?.quantity || 0);
    const charged = calculateChargedQuantity(qty, getItemPromotion(item));
    return charged < qty ? `${label} · pagas ${charged} de ${qty}` : label;
  };

  // Calculate total price
  const total = cartData.reduce(
    (acc, item) => acc + calculateSubtotal(item),
    0
  );

  const trackCartInitiateCheckout = () => {
    if (metaPixelCheckoutTrackedRef.current) return;
    const pixelId = getResolvedMetaPixelId(firstItem?.eventid || {});
    if (!loadMetaPixel(pixelId)) return;
    metaPixelCheckoutTrackedRef.current = true;
    trackMetaPixel("InitiateCheckout", {
      value: Number(discountSuccess.discount ? total - discountSuccess.discount : total) || 0,
      currency: normalizeMetaCurrency(appSettings?.currency_string || appSettings?.currency || "ARS"),
    });
  };

  // Construcción de line items para checkout (múltiples tipos)
  const lineItems = (Array.isArray(cartData) ? cartData : []).map((item) => ({
    cartItemId: item?._id,
    eventid: item?.eventid?._id,
    ticketTypeId: item?.ticketTypeId || item?.tickettypeid || item?.typeid || undefined,
    quantity: item?.quantity || 1,
    unitprice: getItemPrice(item),
    promotion: getItemPromotion(item),
  }));

  const handleUpdateCart = async () => {
    try {
      setLoading(true);
      const res = await updateCartApi(cartData, token);
      if (res.status === 200) {
        toast.success("Carrito actualizado con éxito");
      }
      setLoading(false);
    } catch (error) {
      handleError(error);
      toast.error("Something went wrong");
      setLoading(false);
    }
  };

  const removeCart = async (id) => {
    try {
      setLoading(true);
      const data = { id };
      const res = await removeCartApi(data, token);
      if (res.status === 200) fetchCart();
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookFree = async () => {
    if (!eventId) {
      toast.error("No se encontró ningún evento en el carrito");
      return;
    }
    if (!guardDniForCheckout()) return;
    trackCartInitiateCheckout();
    try {
      setLoading(true);
      // create order with 0 amount
      const payload = {
        // Legacy (compatibilidad)
        eventid: eventId,
        quantity: quantity,
        couponcode: appliedCouponCode || undefined,
        referralToken: referralToken || undefined,
        name: currentUser?.name || "",
        email: currentUser?.email || "",
        phone: currentUser?.phone || "",
        additionalInfo: "",
        state: "",
        // Nuevo: line items del carrito (todos gratis)
        items: lineItems,
      };
      const res = await createOrderApi(payload, token);
      if (res?.status === 200 && res?.data?.isSuccess) {
        toast.success("Entrada reservada con éxito");
        navigate("/myevents");
      } else {
        throw new Error(res?.data?.message || "La reserva falló");
      }
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDiscount = async () => {
    try {
      setDiscountLoading(true);
      const data = { code: discountCode };
      const res = await applyDiscountApi(data, token);
      if (res.status === 200) {
        setDiscountSuccess(res.data.info);
      }
      setDiscountLoading(false);
    } catch (error) {
      handleError(error);

      setDiscountError(error?.response?.data?.message || "Algo salió mal");
      setDiscountLoading(false);
    }
  };

  if (loading || discountLoading) return <SpinLoading />;

  // Solo renderizar si cartData fue cargado (no null/undefined)
  if (!Array.isArray(cartData)) return null;

  // No mostrar sección si el carrito está vacío
  if (cartData.length === 0) {
    return (
      <>
        <Header imageSrc={image} title="Booking Ticket" navigation={nav} />
        <ToastContainer />
        <div className="cart-item-empty-container pading150">
          <span className="cart-empty-text">Carrito vacio :( </span>
          <span
            className="cart-empty-text"
            style={{ cursor: "pointer", color: "#ec6325" }}
            onClick={() => navigate("/event")}
          >
            Ir al Inicio
          </span>
        </div>
      </>
    );
  }

  return (
    <>
      <Header imageSrc={image} title="Compra de entradas" navigation={nav} />
      <ToastContainer />
      <div className="cart-container pading150">
        <div className="cart-item-container">
          <Row style={{ padding: "0px", width: "100%", margin: "0px" }}>
            <Col xs={12} sm={12} md={12} lg={8} className="cart-item-col ">
              <Card className="cart-item-card">
                <Card.Body style={{ padding: "0px" }}>
                  {maxQuantityerror && (
                    <span
                      className="text-danger"
                      style={{ marginLeft: "15px" }}
                    >
                      {maxQuantityerror}
                    </span>
                  )}

                  {/* Desktop table */}
                  <Table className="cart-item-table-desktop">
                    <thead className="cart-item-table-header">
                      <tr className="cart-item-table-row">
                        <th>Remove</th>
                        <th>Event Name</th>
                        <th>Price</th>
                        <th>Quantity</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody className="cart-item-table-body">
                      {cartData.map((item, index) => (
                        <tr className="cart-item-table-row" key={item._id}>
                          <td className="cart-item-table-remove">
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              onClick={() => removeCart(item._id)}
                            >
                              <path
                                d="M13.3002 0.71001C13.2077 0.617307 13.0978 0.543759 12.9768 0.493577C12.8559 0.443396 12.7262 0.417566 12.5952 0.417566C12.4643 0.417566 12.3346 0.443396 12.2136 0.493577C12.0926 0.543759 11.9827 0.617307 11.8902 0.71001L7.00022 5.59001L2.11022 0.70001C2.01764 0.607428 1.90773 0.533988 1.78677 0.483883C1.6658 0.433778 1.53615 0.40799 1.40522 0.40799C1.27429 0.40799 1.14464 0.433778 1.02368 0.483883C0.902716 0.533988 0.792805 0.607428 0.700223 0.70001C0.607642 0.792592 0.534202 0.902502 0.484097 1.02347C0.433992 1.14443 0.408203 1.27408 0.408203 1.40501C0.408203 1.53594 0.433992 1.66559 0.484097 1.78655C0.534202 1.90752 0.607642 2.01743 0.700223 2.11001L5.59022 7.00001L0.700223 11.89C0.607642 11.9826 0.534202 12.0925 0.484097 12.2135C0.433992 12.3344 0.408203 12.4641 0.408203 12.595C0.408203 12.7259 0.433992 12.8556 0.484097 12.9766C0.534202 13.0975 0.607642 13.2074 0.700223 13.3C0.792805 13.3926 0.902716 13.466 1.02368 13.5161C1.14464 13.5662 1.27429 13.592 1.40522 13.592C1.53615 13.592 1.6658 13.5662 1.78677 13.5161C1.90773 13.466 2.01764 13.3926 2.11022 13.3L7.00022 8.41001L11.8902 13.3C11.9828 13.3926 12.0927 13.466 12.2137 13.5161C12.3346 13.5662 12.4643 13.592 12.5952 13.592C12.7262 13.592 12.8558 13.5662 12.9768 13.5161C13.0977 13.466 13.2076 13.3926 13.3002 13.3C13.3928 13.2074 13.4662 13.0975 13.5163 12.9766C13.5665 12.8556 13.5922 12.7259 13.5922 12.595C13.5922 12.4641 13.5665 12.3344 13.5163 12.2135C13.4662 12.0925 13.3928 11.9826 13.3002 11.89L8.41022 7.00001L13.3002 2.11001C13.6802 1.73001 13.6802 1.09001 13.3002 0.71001Z"
                                fill="#ffffff"
                              />
                            </svg>
                          </td>
                          <td>
                            <span className="cart-item-table-event-name">
                              {item.eventid.name}
                            </span>
                            {(() => {
                              const typeName = item?.ticketTypeName || item?.tickettype?.name || item?.type?.name || item?.typename;
                              return typeName ? (
                                <div className="cart-item-table-type-name" style={{ fontSize: 12, color: '#666' }}>
                                  {typeName}
                                  {Number(item?.ticketsPerUnit || 1) > 1 ? ` · incluye ${Number(item.ticketsPerUnit || 1)} entradas` : ""}
                                </div>
                              ) : null;
                            })()}
                            {getItemPromotionNote(item) && (
                              <div className="cart-item-table-type-name" style={{ fontSize: 12, color: '#7f8d00', fontWeight: 700 }}>
                                {getItemPromotionNote(item)}
                              </div>
                            )}
                          </td>
                          <td>
                            <span className="cart-item-table-price">
                              {appSettings.currency}
                              {getItemPrice(item).toFixed(2)}
                            </span>
                          </td>
                          <td className="cart-item-table-quantity">
                            <Form.Control
                              min={1}
                              max={maxTicketsPerUser}
                              type="number"
                              className="cart-item-table-input"
                              value={item.quantity}
                              onChange={(e) =>
                                handleQuantityChange(
                                  index,
                                  parseInt(e.target.value)
                                )
                              }
                            />
                          </td>
                          <td>
                            <span className="cart-item-table-price">
                              {appSettings.currency}
                              {calculateSubtotal(item).toFixed(2)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {/* Mobile table */}
                  <Table className="cart-item-table-mobile">
                    <tbody className="cart-item-table-body">
                      {cartData.map((item, index) => (
                        <tbody key={item._id}>
                          <tr>
                            <td
                              style={{
                                padding: "15px",
                                backgroundColor: "#F9FCFF",
                              }}
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                onClick={() => removeCart(item._id)}
                              >
                                <path
                                  d="M13.3002 0.71001C13.2077 0.617307 13.0978 0.543759 12.9768 0.493577C12.8559 0.443396 12.7262 0.417566 12.5952 0.417566C12.4643 0.417566 12.3346 0.443396 12.2136 0.493577C12.0926 0.543759 11.9827 0.617307 11.8902 0.71001L7.00022 5.59001L2.11022 0.70001C2.01764 0.607428 1.90773 0.533988 1.78677 0.483883C1.6658 0.433778 1.53615 0.40799 1.40522 0.40799C1.27429 0.40799 1.14464 0.433778 1.02368 0.483883C0.902716 0.533988 0.792805 0.607428 0.700223 0.70001C0.607642 0.792592 0.534202 0.902502 0.484097 1.02347C0.433992 1.14443 0.408203 1.27408 0.408203 1.40501C0.408203 1.53594 0.433992 1.66559 0.484097 1.78655C0.534202 1.90752 0.607642 2.01743 0.700223 2.11001L5.59022 7.00001L0.700223 11.89C0.607642 11.9826 0.534202 12.0925 0.484097 12.2135C0.433992 12.3344 0.408203 12.4641 0.408203 12.595C0.408203 12.7259 0.433992 12.8556 0.484097 12.9766C0.534202 13.0975 0.607642 13.2074 0.700223 13.3C0.792805 13.3926 0.902716 13.466 1.02368 13.5161C1.14464 13.5662 1.27429 13.592 1.40522 13.592C1.53615 13.592 1.6658 13.5662 1.78677 13.5161C1.90773 13.466 2.01764 13.3926 2.11022 13.3L7.00022 8.41001L11.8902 13.3C11.9828 13.3926 12.0927 13.466 12.2137 13.5161C12.3346 13.5662 12.4643 13.592 12.5952 13.592C12.7262 13.592 12.8558 13.5662 12.9768 13.5161C13.0977 13.466 13.2076 13.3926 13.3002 13.3C13.3928 13.2074 13.4662 13.0975 13.5163 12.9766C13.5665 12.8556 13.5922 12.7259 13.5922 12.595C13.5922 12.4641 13.5665 12.3344 13.5163 12.2135C13.4662 12.0925 13.3928 11.9826 13.3002 11.89L8.41022 7.00001L13.3002 2.11001C13.6802 1.73001 13.6802 1.09001 13.3002 0.71001Z"
                                  fill="#ffffff"
                                />
                              </svg>
                            </td>
                          </tr>
                          <tr className="cart-item-table-row">
                            <td className="cart-item-table-title-mobile">
                              Event Name
                            </td>
                            <td>
                              <span className="cart-item-table-event-name">
                                {item.eventid.name}
                              </span>
                              {(() => {
                                const typeName = item?.ticketTypeName || item?.tickettype?.name || item?.type?.name || item?.typename;
                              return typeName ? (
                                <div className="cart-item-table-type-name" style={{ fontSize: 12, color: '#666' }}>
                                  {typeName}
                                  {Number(item?.ticketsPerUnit || 1) > 1 ? ` · incluye ${Number(item.ticketsPerUnit || 1)} entradas` : ""}
                                </div>
                              ) : null;
                            })()}
                              {getItemPromotionNote(item) && (
                                <div className="cart-item-table-type-name" style={{ fontSize: 12, color: '#7f8d00', fontWeight: 700 }}>
                                  {getItemPromotionNote(item)}
                                </div>
                              )}
                            </td>
                          </tr>
                          <tr className="cart-item-table-row">
                            <td className="cart-item-table-title-mobile">
                              Price
                            </td>
                            <td>
                              <span className="cart-item-table-price">
                                {appSettings.currency}
                                {getItemPrice(item).toFixed(2)}
                              </span>
                            </td>
                          </tr>
                          <tr className=" cart-item-table-row cart-item-table-row-quantity">
                            <td className="cart-item-table-title-mobile">
                              Quantity
                            </td>
                            <td className="cart-item-table-quantity">
                              <Form.Control
                                min={1}
                                max={maxTicketsPerUser}
                                type="number"
                                className="cart-item-table-input"
                                value={item.quantity}
                                onChange={(e) =>
                                  handleQuantityChange(
                                    index,
                                    parseInt(e.target.value)
                                  )
                                }
                              />
                            </td>
                          </tr>
                          <tr className="cart-item-table-row">
                            <td className="cart-item-table-title-mobile">
                              Total
                            </td>
                            <td>
                              <span className="cart-item-table-price">
                                {appSettings.currency}
                                {calculateSubtotal(item).toFixed(2)}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      ))}
                    </tbody>
                  </Table>

                  <div className="cart-item-update-button-container">
                    <button
                      className="cart-item-update-button"
                      onClick={handleUpdateCart}
                      disabled={loading}
                    >
                      <span className="cart-item-update-button-transition"></span>
                      <span className="label">
                        {loading ? (
                          <Spinner
                            animation="border"
                            variant="light"
                            size="sm"
                          />
                        ) : (
                          "Actulizar carrito"
                        )}
                      </span>
                    </button>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={12} md={12} lg={4} className="cart-item-col">
              <Card className="cart-item-card1">
                <Card.Body style={{ padding: "0px" }}>
                  <div className="cart-discount-code-container">
                    {total > 0 && (
                      <>
                        <span className="cart-discount-code-text">
                          Codigo de descuento
                        </span>
                        <span className="cart-discount-code-input">
                          <Form.Control
                            className="cart-discount-code-input-control"
                            type="text"
                            placeholder="Codigo de descuento"
                            value={discountCode}
                            onChange={(e) => {
                              setDiscountCode(e.target.value);
                              setDiscountError("");
                            }}
                          />
                          {discountError !== "" &&
                            !discountSuccess.discount && (
                              <span className="text-danger">
                                {discountError}
                              </span>
                            )}
                          {discountSuccess.discount && (
                            <span className="text-success">
                              Decuento aplicado
                            </span>
                          )}
                        </span>
                        <button
                          className="cart-discount-code-button"
                          disabled={discountSuccess.discount || discountLoading}
                          onClick={handleApplyDiscount}
                        >
                          {discountLoading ? (
                            <Spinner
                              animation="border"
                              variant="light"
                              size="sm"
                            />
                          ) : (
                            "Apply"
                          )}
                        </button>
                      </>
                    )}
                    <span className="cart-discount-code-text">Cart Total</span>
                  </div>

                  <div
                    className="cart-discount-code-total-container"
                    style={{ borderTop: "1px solid #c7d5e1" }}
                  >
                    <span className="cart-discount-code-total-text">
                      Subtotal
                    </span>
                    <span className="cart-discount-code-total-price">
                      {appSettings.currency}
                      {total.toFixed(2)}
                    </span>
                  </div>
                  <div className="cart-discount-code-total-container">
                    <span className="cart-discount-code-total-text">
                      Discount
                    </span>
                    <span className="cart-discount-code-total-price">
                      -{appSettings.currency}
                      {discountSuccess.discount
                        ? discountSuccess.discount.toFixed(2)
                        : (0.0).toFixed(2)}
                    </span>
                  </div>
                  <div className="cart-discount-code-total-container">
                    <span className="cart-discount-code-total-text">Total</span>
                    <span className="cart-discount-code-total-price">
                      {appSettings.currency}
                      {discountSuccess.discount
                        ? (total - discountSuccess.discount).toFixed(2)
                        : total.toFixed(2)}
                    </span>
                  </div>

                  {/* === Acciones de checkout === */}
                  <div className="cart-discount-code-button-container">
                    {needsDniForCheckout() ? (
                      <button
                        className="cart-discount-code-button btn"
                        style={{ marginTop: 12 }}
                        onClick={() => navigate("/account/profile")}
                      >
                        Completar DNI para continuar
                      </button>
                    ) : isFreeEvent ? (
                      <button
                        className="cart-discount-code-button btn"
                        style={{ marginTop: 12 }}
                        onClick={handleBookFree}
                        disabled={!eventId}
                      >
                        Confirmar entrada gratuita
                      </button>
                    ) : (
                      <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                        <CloverCheckoutButton
                          eventId={eventId}
                          quantity={quantity}
                          couponcode={appliedCouponCode}
                          referralToken={referralToken}
                          name={currentUser?.name || ""}
                          email={currentUser?.email || ""}
                          phone={currentUser?.phone || ""}
                          additionalInfo=""
                          state=""
                          authToken={token}
                          items={lineItems}
                          onInitiateCheckout={trackCartInitiateCheckout}
                        />
                        <FiservCheckoutButton
                          eventId={eventId}
                          quantity={quantity}
                          couponcode={appliedCouponCode}
                          referralToken={referralToken}
                          name={currentUser?.name || ""}
                          email={currentUser?.email || ""}
                          phone={currentUser?.phone || ""}
                          additionalInfo=""
                          state=""
                          authToken={token}
                          installments={{ count: 3 }} // si querés ofrecer cuotas
                          items={lineItems}
                          onInitiateCheckout={trackCartInitiateCheckout}
                        />
                      </div>
                    )}
                  </div>
                  {/* === /Acciones de checkout === */}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export default Cart;
